import dayjs from 'utils/dayjs';

const INVALID_DATE = 'Invalid Date';
const UPDATED_DATE_FORMAT = { month: 'long', day: 'numeric', year: 'numeric' };
const DATETIME_LOCAL = 'YYYY-MM-DDTHH:mm';

const WEEKDAY_INDEX = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// dayjs customParseFormat does not support parsing standalone weekday names (dddd).
// This helper creates a real date for a given weekday name + time, enabling timezone conversion.
const weekdayToDate = (weekday, time, timezone) => {
  const targetDayIndex = WEEKDAY_INDEX[weekday];
  const base = dayjs.tz(dayjs(), timezone).startOf('week');
  const dateStr = base.add(targetDayIndex, 'day').format('YYYY-MM-DD');

  return dayjs.tz(`${dateStr} ${time}`, 'YYYY-MM-DD hh:mma', timezone);
};

const convertTimeByZone = (dateTime, format, sourceTimezone = 'UTC', destTimezone = 'UTC') => {
  const dateTimeInSourceTimezone = dayjs.tz(dateTime, format, sourceTimezone);

  return dayjs.tz(dateTimeInSourceTimezone, destTimezone).format(format);
};

const combineLimitedTimeSlotDateTime = (date, time) => `${dayjs(new Date(date)).format('YYYY-MM-DD')} ${time}`;

export const formatUpdatedDate = (date) => {
  const formatedDate = new Date(date).toLocaleString('en-US', UPDATED_DATE_FORMAT);

  return formatedDate === INVALID_DATE ? date : formatedDate;
};

export const formatCallRequestDisplayTime = (d, timeZone = 'UTC') => {
  const date = new Date(d);

  const formatWithTimezone = (formatPattern) => dayjs(date).tz(timeZone).format(formatPattern);

  return (
    date !== INVALID_DATE && {
      time: formatWithTimezone('hh:mm A'),
      month: formatWithTimezone('MMM'),
      date: formatWithTimezone('DD'),
      monthDate: formatWithTimezone('MMM Do'),
      weekday: formatWithTimezone('ddd'),
      zone: formatWithTimezone('z'),
    }
  );
};

export const getDistanceTimeAgo = (d, timeZone = 'UTC') => {
  const date = new Date(d);

  return date !== INVALID_DATE && dayjs(date).tz(timeZone).fromNow();
};

export const formatDateTimeCompletedCall = (d, timeZone = 'UTC') => {
  const date = new Date(d);

  return date !== INVALID_DATE && dayjs(date).tz(timeZone).format('MMM DD, YYYY hh:mm A');
};

export const isAfterOneHourFromNow = (time) => dayjs(time).isAfter(dayjs(new Date()).add(1, 'hour'));

export const isValidTimeSlot = ({ startTime, endTime }) => dayjs(endTime, 'hh:mma').isAfter(dayjs(startTime, 'hh:mma'));

export const convertLimitedTimeSlot = (timeSlot, inputTimezone = 'UTC', targetTimezone = 'UTC') => {
  const { startDate, startTime, endDate = new Date(), endTime } = timeSlot;

  const convertedStartTime = convertTimeByZone(
    combineLimitedTimeSlotDateTime(startDate, startTime),
    'YYYY-MM-DD hh:mma',
    inputTimezone,
    targetTimezone,
  ).split(' ');

  const convertedEndTime = convertTimeByZone(
    combineLimitedTimeSlotDateTime(endDate, endTime),
    'YYYY-MM-DD hh:mma',
    inputTimezone,
    targetTimezone,
  ).split(' ');

  return {
    startDate: convertedStartTime[0],
    startTime: convertedStartTime[1],
    endDate: convertedEndTime[0],
    endTime: convertedEndTime[1],
  };
};

export const convertScheduledTimeSlot = (
  { days, startTime, endTime },
  inputTimezone = 'UTC',
  targetTimezone = 'UTC',
) => {
  const convertWeekday = (weekday) => {
    const sourceDate = weekdayToDate(weekday, startTime, inputTimezone);
    const converted = dayjs.tz(sourceDate, targetTimezone);

    return WEEKDAY_NAMES[converted.day()];
  };

  const fullDays = days.map(convertWeekday);
  const indexDays = fullDays.map((fullDay) => WEEKDAY_INDEX[fullDay]);

  return {
    indexDays,
    days: fullDays,
    startTime: convertTimeByZone(startTime, 'hh:mma', inputTimezone, targetTimezone),
    endTime: convertTimeByZone(endTime, 'hh:mma', inputTimezone, targetTimezone),
  };
};

export const convertSpecificTime = (datetime, sourceTimezone = 'UTC', destTimezone = 'UTC') => {
  const formatStr = DATETIME_LOCAL;

  // exclude timezone when source is not UTC
  const formatedTime = sourceTimezone === 'UTC' ? datetime : dayjs(datetime).format(formatStr);

  return convertTimeByZone(formatedTime, formatStr, sourceTimezone, destTimezone);
};

export const formatDate = (date) => date && dayjs(date).format('MMMM DD, YYYY');

export const isActiveAuthenticatedUser = (currentSignInAt) =>
  dayjs(currentSignInAt).isAfter(dayjs().subtract(15, 'minute'));

export const isBeforeEndInPersonMeeting = (scheduledAt, duration) =>
  dayjs().isBefore(dayjs(scheduledAt).add(duration, 'minute'));

export const convertTimeSlotForProposedTime = (timeSlot, srcTimezone = 'UTC', destTimezone = 'UTC') => {
  const normalizedTimeSlot = timeSlot.replace(/\s/g, '').toLowerCase();

  return convertTimeByZone(normalizedTimeSlot, 'hh:mma', srcTimezone, destTimezone);
};

export const convertCombinedProposedTime = (date, timeSlot, currentTimezone) => {
  const formatedDate = date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
  const datetime = `${formatedDate} ${timeSlot}`;

  const convertedDateTime = convertTimeByZone(datetime, 'YYYY-M-D hh:mma', currentTimezone).split(' ');

  return {
    date: convertedDateTime[0],
    time: convertedDateTime[1],
  };
};

export const isCallRequestStartingIn24HoursOrLess = (scheduledAt) => dayjs(scheduledAt).isBefore(dayjs().add(1, 'day'));

export const isAvailableToJoinSession = (scheduledAt) => dayjs(scheduledAt).isBefore(dayjs().add(5, 'minute'));

export const isPastDeadline = (deadline) => deadline && dayjs(deadline).isBefore(dayjs());
